// LWC v4 → v5 compat shim. v5 removed the convenience series-factory
// methods (addBarSeries, addLineSeries, addCandlestickSeries,
// addHistogramSeries, addAreaSeries) in favor of a single unified
// chart.addSeries(definition, options, paneIndex) API. The canonical
// TradeChart, StrategyChart, and their indicator definitions still use the v4
// method names; this shim re-attaches them as forwarders during the v5
// migration.
//
// Verified against lightweight-charts@5.2.0 typings 2026-05-11:
//   - chart.addSeries(SeriesDefinition, options, paneIndex?) is the v5 API.
//   - LineSeries / CandlestickSeries / HistogramSeries / AreaSeries are
//     exported as SeriesDefinition constants from 'lightweight-charts'.
//
// Remove this shim once all remaining chart code uses addSeries directly.

import {
  createChart as lwcCreateChart,
  AreaSeries,
  BarSeries,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
} from 'lightweight-charts'

/**
 * Drop-in replacement for `createChart` from 'lightweight-charts' that
 * attaches v4-style series-factory methods to the returned chart so
 * existing v4 call sites keep working under LWC v5.
 *
 * Each forwarder accepts the v5 positional `paneIndex` as its second
 * argument — v4 callers passing a single argument get pane 0 implicitly,
 * matching legacy v4 behavior. New-chassis call sites can pass paneIndex
 * to opt into multi-pane rendering.
 *
 * @param {string | HTMLElement} container
 * @param {object} [options]
 * @returns {import('lightweight-charts').IChartApi}
 */
export function createChart(container, options) {
  const chart = lwcCreateChart(container, options)
  chart.addLineSeries = (opts, paneIdx) => chart.addSeries(LineSeries, opts, paneIdx)
  chart.addCandlestickSeries = (opts, paneIdx) => chart.addSeries(CandlestickSeries, opts, paneIdx)
  chart.addBarSeries = (opts, paneIdx) => chart.addSeries(BarSeries, opts, paneIdx)
  chart.addHistogramSeries = (opts, paneIdx) => chart.addSeries(HistogramSeries, opts, paneIdx)
  chart.addAreaSeries = (opts, paneIdx) => chart.addSeries(AreaSeries, opts, paneIdx)
  return chart
}

// Re-export everything else from lightweight-charts unchanged so callers
// can do a single import from this module:
//   import { createChart, LineStyle, ColorType, ... } from '<path>/createChartCompat'
export * from 'lightweight-charts'
